import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Select from '@mui/material/Select';
import { alpha } from '@mui/material/styles';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';

import { firstImage, ListingThumbnail } from 'src/components/listing-row';

import { matchReason, matchDetails, DECISION_LABEL } from './decisions';

// The pictures on the row. Big enough to recognise the item, small enough that a screenful of them
// is a list a seller can run their eye down.
const THUMBNAIL_SIZE = 48;

// Wide enough for the longest thing the control ever says, so the rows line up.
const CONTROL_WIDTH = { xs: 128, sm: 150 };

// A shared SKU is the reason a seller can act on without looking twice, so it is the one that gets
// a colour. A shared title is worth a second look, and reads as the plainer of the two.
const REASON_CHIP_COLOR = { sku: 'info', title: 'default' };

/**
 * One item an import found that looks like something the seller already has: the two pictures side
 * by side, what the item is, why it looked like a match, and what to do about it.
 *
 * Nothing is worked out here — the review page owns every decision and hands this row the one it
 * currently holds.
 */
export default function MatchRow ({
  match,
  marketplaceName,
  choices,
  defaultChoice,
  decision,
  onChange,
  divided,
}) {
  const incoming = match?.newListing || {};
  const existing = match?.existingListing || {};
  const title = incoming.title || 'Untitled item';

  const incomingImage = firstImage(incoming);
  const existingImage = firstImage(existing);
  // The two sides are meant to be the same item, so the same picture twice would say nothing.
  const secondImage = existingImage && existingImage !== incomingImage ? existingImage : null;

  const reason = matchReason(match);
  const details = matchDetails(match, marketplaceName);

  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{
        px: 2,
        py: 1.5,
        height: 1,
        borderTop: divided ? (theme) => `1px solid ${theme.palette.divider}` : 'none',
        // A row the seller has changed is tinted, so a long list shows its own edits at a glance.
        bgcolor: (theme) =>
          decision === defaultChoice ? 'transparent' : alpha(theme.palette.primary.main, 0.08),
      }}
    >
      {/* Enough of a gap that two pictures read as two items rather than one wide one. */}
      <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
        <ListingThumbnail src={incomingImage} alt={title} size={THUMBNAIL_SIZE} rounded={1} />

        {secondImage ? (
          <ListingThumbnail
            src={secondImage}
            alt={existing.title || title}
            size={THUMBNAIL_SIZE}
            rounded={1}
          />
        ) : null}
      </Stack>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" noWrap title={title}>
          {title}
        </Typography>

        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0, mt: 0.25 }}>
          <Chip
            size="small"
            variant="soft"
            color={REASON_CHIP_COLOR[reason.tone]}
            label={reason.label}
            // Marketplaces generate SKUs long enough to outgrow the row, so the whole reason is
            // kept on hover — a seller checking a match needs to be able to read the SKU.
            title={reason.label}
            sx={{ height: 20, flexShrink: 0, maxWidth: 220, '& .MuiChip-label': { px: 0.75 } }}
          />

          {details ? (
            <Typography variant="caption" color="text.secondary" noWrap title={details}>
              {details}
            </Typography>
          ) : null}
        </Stack>
      </Box>

      <Select
        size="small"
        value={decision}
        onChange={(event) => onChange(event.target.value)}
        renderValue={(value) => DECISION_LABEL[value]?.short}
        sx={{ flexShrink: 0, width: CONTROL_WIDTH, bgcolor: 'background.paper' }}
      >
        {choices.map((choice) => (
          <MenuItem key={choice} value={choice}>
            {DECISION_LABEL[choice]?.long}
          </MenuItem>
        ))}
      </Select>
    </Stack>
  );
}

MatchRow.propTypes = {
  match: PropTypes.shape({
    newListing: PropTypes.object,
    existingListing: PropTypes.object,
    matchReasons: PropTypes.array,
  }).isRequired,
  marketplaceName: PropTypes.string,
  choices: PropTypes.array.isRequired,
  defaultChoice: PropTypes.string.isRequired,
  decision: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  // False on the first row, so the list's own top edge is the only line above it.
  divided: PropTypes.bool,
};
